// src/lib/siteHosts.js
//
// Suporte ao domínio espelho consultamais.org (landing dos anúncios Google Ads).
//
// Estratégia (jul/2026): o .org é servido pelo MESMO build/hosting Firebase que o
// consultamais.app. O visitante que digita consultamais.org vê a home normalmente
// (URL continua .org — requisito para o crawler de política do Ads não cair no .app),
// O cadastro até o aceite (nome → telefone/termos) pode ficar no .org — o CTA
// da landing cai em /consulta. Pagamento, triagem e chat vão para
// https://consultamais.app preservando gclid/UTMs, para a conversão contar
// na tag do Google Ads. A Home no .org omite "receita"/"medicamento"
// (política de medicamentos controlados, jul/2026).
//
// Ver docs/ads/org-firebase-setup.md.

export const APP_ORIGIN = 'https://consultamais.app';

/** Origem pública da landing dos anúncios (mesmo site Firebase do hosting:org). */
export const ORG_ORIGIN = 'https://promo.consultamais.org';

/**
 * Origem para canonical / og:url. No .org o AdsBot trata canonical em
 * consultamais.app como Destino não correspondente.
 */
export function publicOriginForSeo() {
  if (typeof window !== 'undefined' && isOrgHost()) {
    return String(window.location.origin || ORG_ORIGIN).replace(/\/+$/, '') || ORG_ORIGIN;
  }
  return APP_ORIGIN;
}

// Parâmetros de atribuição que precisam sobreviver ao pulo .org → .app.
// gtag lê gclid/gbraid/wbraid da URL de destino e grava o cookie _gcl_* no .app.
const TRACKED_PARAMS = [
  'gclid',
  'gbraid',
  'wbraid',
  'gclsrc',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'em', // hash anonimo do CTA infomercial
];

const STORAGE_KEY = 'orgLandingParams';

function previewOrgAds() {
  try {
    if (typeof window === 'undefined') return false;
    const q = new URLSearchParams(window.location.search).get('org_ads');
    if (q === '1' || q === 'true') {
      sessionStorage.setItem('orgAdsPreview', '1');
      return true;
    }
    return sessionStorage.getItem('orgAdsPreview') === '1';
  } catch {
    return false;
  }
}

export function isOrgHost(hostname) {
  if (!hostname && previewOrgAds()) return true;
  const h = hostname || (typeof window !== 'undefined' ? window.location.hostname : '');
  return /(^|\.)consultamais\.org$/i.test(h);
}

/**
 * Captura gclid/UTMs da URL de chegada no .org e persiste em sessionStorage.
 * Necessário porque a navegação SPA (navigate('/consulta')) descarta a query
 * string — sem isso o gclid se perderia antes do pulo para o .app.
 */
export function captureLandingParams() {
  if (!isOrgHost()) return;
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    for (const key of TRACKED_PARAMS) {
      const value = params.get(key);
      if (value) captured[key] = value;
    }
    if (Object.keys(captured).length === 0) return;
    const existing = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...existing, ...captured }));
  } catch {}
}

/**
 * Monta a URL absoluta no consultamais.app para o caminho dado, reanexando os
 * parâmetros de atribuição capturados na chegada (sem sobrescrever parâmetros
 * já presentes no caminho).
 *
 * @param {string} pathWithSearch ex.: "/consulta" ou "/consulta?cupom=X"
 */
export function buildAppUrl(pathWithSearch = '/') {
  const url = new URL(pathWithSearch, APP_ORIGIN);
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
    for (const [key, value] of Object.entries(stored)) {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    }
  } catch {}
  return url.toString();
}

/**
 * CTA da landing .org: o funil (cadastro, pagamento, chat) vive no .app.
 * Ficar no .org quebra CORS do apiV2 e perde a sessão no pulo de domínio.
 * Devolve true se saiu do .org (o caller não deve navegar no SPA).
 */
export function maybeJumpOrgSignupToApp() {
  if (!isOrgHost()) return false;
  captureLandingParams();
  if (typeof window !== 'undefined') {
    window.location.assign(buildAppUrl('/consulta'));
  }
  return true;
}

// Caminhos que PODEM ser servidos no .org (conteúdo institucional/landing).
// Tudo fora desta lista redireciona para o .app — o funil transacional
// (consulta, pagamento, triagem, chat, painéis) vive apenas no .app.
const ORG_ALLOWED_EXACT = new Set([
  '/',
  '/home',
  '/reviews',
  '/diretor',
  '/lgpd',
  '/termos-de-uso',
  '/transparencia-ia',
  '/dados-exclusao',
  '/conteudo',
  '/noticias',
  '/marli',
  '/marli/cupom',
  '/marli/cupom-gratuito',
  '/marli/termos',
  '/cupom',
  '/cupom/termos',
  '/social',
  '/social/cupom',
  '/social/cupom-gratuito',
  '/social/termos',
  // Hubs de descoberta GEO (ago/2026) — conteudo institucional publico.
  '/consulta-medica-por-chat',
  '/plataformas-teleconsulta-chat',
  '/consulta-mais-e-consulta-plus',
  '/ec-cmchatmedico',
  '/mp-cmchatmedico',
  '/cmchatmedico',
  '/imprensa',
  // URL final histórica dos RSAs Search-6. Sem isto o guard pula para
  // consultamais.app/promo (domínio diferente → Destino não correspondente).
  // O funil /consulta…/termos NÃO fica no .org: CORS do apiV2 recusa
  // promo.consultamais.org e o LS não atravessa para o .app.
  '/promo',
  '/promo2',
]);

const ORG_ALLOWED_PREFIXES = ['/conteudo/'];

export function isOrgAllowedPath(pathname) {
  const clean = (pathname || '/').replace(/\/+$/, '') || '/';
  if (/receita|medicamento|prescri/i.test(clean)) return false;
  if (ORG_ALLOWED_EXACT.has(clean)) return true;
  return ORG_ALLOWED_PREFIXES.some((prefix) => clean.startsWith(prefix));
}
